"use client";

import { useState } from "react";
import Image from "next/image";
import { Dialog } from "@/components/ui/Dialog";
import { Reveal } from "@/components/motion";
import type { AboutGalleryContent } from "@/lib/api-types";

interface ImpactGalleryStripProps {
  gallery: AboutGalleryContent;
}

export function ImpactGalleryStrip({ gallery }: ImpactGalleryStripProps) {
  const [selected, setSelected] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  const photos = gallery.images;

  return (
    <Reveal className="mt-20 border-t border-border pt-12">
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-bold text-primary md:text-3xl">
          {gallery.title}
        </h2>
        <p className="max-w-xl text-muted">{gallery.subtitle}</p>
      </div>
      <div className="film-strip -mx-4 px-4 md:-mx-6 md:px-6">
        <div className="scrollbar-none flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory">
          {photos.map((photo, index) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => setSelected({ src: photo.src, alt: photo.alt })}
              className="relative shrink-0 snap-start overflow-hidden rounded-2xl border border-border transition-shadow hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              style={{
                width: index % 3 === 1 ? "280px" : "240px",
                height: index % 3 === 1 ? "320px" : "260px",
              }}
              aria-label={`View impact photo ${index + 1}`}
            >
              <Image
                src={photo.src}
                alt={photo.alt || `Community impact photo ${index + 1}`}
                fill
                className="object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-[1.03]"
                sizes="280px"
              />
            </button>
          ))}
        </div>
      </div>

      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={gallery.title}
        className="max-w-3xl"
      >
        {selected && (
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl">
            <Image
              src={selected.src}
              alt={selected.alt || gallery.title}
              fill
              className="object-cover"
              sizes="(max-width:768px) 100vw, 768px"
            />
          </div>
        )}
      </Dialog>
    </Reveal>
  );
}
